import Button from '@/common/components/Button'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import MaskedTextField from '@/common/components/MaskedTextField'
import { customUnitMask, numberMask } from '@/common/constants/masks.constant'
import { CompanyBranchValidationSchema } from '@/features/companyBranches/schemas/companyBranch.schema'
import { TrashIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

interface BranchConsumptionRestrictionProps {
  disabled?: boolean
}

export const BranchConsumptionRestriction = ({ disabled }: BranchConsumptionRestrictionProps) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<CompanyBranchValidationSchema>()

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col gap-y-4">
        <div>
          <p className="text-xs">Intervalo de tempo mínimo entre consumos</p>

          <ErrorMessage
            name="minimum_seconds_interval_between_consumptions"
            message={errors?.minimum_seconds_interval_between_consumptions?.root?.message || ''}
          />
        </div>

        <div className="flex gap-x-2">
          <MaskedTextField
            className="grow"
            mask={numberMask(30)}
            label="Dias"
            {...register('minimum_seconds_interval_between_consumptions.days')}
            errorMessage={errors?.minimum_seconds_interval_between_consumptions?.days?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(23)}
            label="Horas"
            {...register('minimum_seconds_interval_between_consumptions.hours')}
            errorMessage={errors?.minimum_seconds_interval_between_consumptions?.hours?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(59)}
            label="Minutos"
            {...register('minimum_seconds_interval_between_consumptions.minutes')}
            errorMessage={errors?.minimum_seconds_interval_between_consumptions?.minutes?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(59)}
            label="Segundos"
            {...register('minimum_seconds_interval_between_consumptions.seconds')}
            errorMessage={errors?.minimum_seconds_interval_between_consumptions?.seconds?.message}
            disabled={disabled}
          />
        </div>

        <div className="flex justify-between">
          <p className="text-xs font-light italic">Limite mínimo de tempo: 1 minuto</p>

          <Button
            variant="neutral"
            className="text-white-800"
            onClick={() => {
              setValue('minimum_seconds_interval_between_consumptions.days', undefined)
              setValue('minimum_seconds_interval_between_consumptions.hours', undefined)
              setValue('minimum_seconds_interval_between_consumptions.minutes', undefined)
              setValue('minimum_seconds_interval_between_consumptions.seconds', undefined)
            }}
          >
            <TrashIcon strokeWidth={1} className="size-4" />

            <p>Limpar campos</p>
          </Button>
        </div>
      </div>

      <MaskedTextField
        className="grow"
        mask={customUnitMask('Km', 999999, false)}
        label="Intervalo Mínimo de Quilometragem entre consumos"
        {...register('minimum_kilometers_usage_between_consumptions')}
        errorMessage={errors?.minimum_kilometers_usage_between_consumptions?.message}
        disabled={disabled}
      />

      <MaskedTextField
        className="grow"
        mask={customUnitMask('Km', 999999, false)}
        label="Intervalo Máximo de Quilometragem entre consumos"
        {...register('maximum_kilometers_usage_between_consumptions')}
        errorMessage={errors?.maximum_kilometers_usage_between_consumptions?.message}
        disabled={disabled}
      />

      <div className="flex flex-col gap-y-4">
        <div>
          <p className="text-xs">Intervalo mínimo de Hora-Uso entre consumos</p>

          <ErrorMessage
            name="minimum_seconds_usage_between_consumptions"
            message={errors?.minimum_seconds_usage_between_consumptions?.root?.message || ''}
          />
        </div>

        <div className="flex gap-x-2">
          <MaskedTextField
            className="grow"
            mask={numberMask(30)}
            label="Dias"
            {...register('minimum_seconds_usage_between_consumptions.days')}
            errorMessage={errors?.minimum_seconds_usage_between_consumptions?.days?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(23)}
            label="Horas"
            {...register('minimum_seconds_usage_between_consumptions.hours')}
            errorMessage={errors?.minimum_seconds_usage_between_consumptions?.hours?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(59)}
            label="Minutos"
            {...register('minimum_seconds_usage_between_consumptions.minutes')}
            errorMessage={errors?.minimum_seconds_usage_between_consumptions?.minutes?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(59)}
            label="Segundos"
            {...register('minimum_seconds_usage_between_consumptions.seconds')}
            errorMessage={errors?.minimum_seconds_usage_between_consumptions?.seconds?.message}
            disabled={disabled}
          />
        </div>

        <div className="flex justify-between">
          <p className="text-xs font-light italic">Limite mínimo de tempo: 1 minuto</p>

          <Button
            variant="neutral"
            className="text-white-800"
            onClick={() => {
              setValue('minimum_seconds_usage_between_consumptions.days', undefined)
              setValue('minimum_seconds_usage_between_consumptions.hours', undefined)
              setValue('minimum_seconds_usage_between_consumptions.minutes', undefined)
              setValue('minimum_seconds_usage_between_consumptions.seconds', undefined)
            }}
          >
            <TrashIcon strokeWidth={1} className="size-4" />

            <p>Limpar campos</p>
          </Button>
        </div>

        <div>
          <p className="text-xs">Intervalo máximo de Hora-Uso entre consumos</p>

          <ErrorMessage
            name="maximum_seconds_usage_between_consumptions"
            message={errors?.maximum_seconds_usage_between_consumptions?.root?.message || ''}
          />
        </div>

        <div className="flex gap-x-2">
          <MaskedTextField
            className="grow"
            mask={numberMask(30)}
            label="Dias"
            {...register('maximum_seconds_usage_between_consumptions.days')}
            errorMessage={errors?.maximum_seconds_usage_between_consumptions?.days?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(23)}
            label="Horas"
            {...register('maximum_seconds_usage_between_consumptions.hours')}
            errorMessage={errors?.maximum_seconds_usage_between_consumptions?.hours?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(59)}
            label="Minutos"
            {...register('maximum_seconds_usage_between_consumptions.minutes')}
            errorMessage={errors?.maximum_seconds_usage_between_consumptions?.minutes?.message}
            disabled={disabled}
          />

          <MaskedTextField
            className="grow"
            mask={numberMask(59)}
            label="Segundos"
            {...register('maximum_seconds_usage_between_consumptions.seconds')}
            errorMessage={errors?.maximum_seconds_usage_between_consumptions?.seconds?.message}
            disabled={disabled}
          />
        </div>

        <div className="flex justify-between">
          <p className="text-xs font-light italic">Limite mínimo de tempo: 1 minuto</p>

          <Button
            variant="neutral"
            className="text-white-800"
            onClick={() => {
              setValue('maximum_seconds_usage_between_consumptions.days', undefined)
              setValue('maximum_seconds_usage_between_consumptions.hours', undefined)
              setValue('maximum_seconds_usage_between_consumptions.minutes', undefined)
              setValue('maximum_seconds_usage_between_consumptions.seconds', undefined)
            }}
          >
            <TrashIcon strokeWidth={1} className="size-4" />

            <p>Limpar campos</p>
          </Button>
        </div>
      </div>
    </div>
  )
}
